import React, { useCallback, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce, get } from 'lodash';
import { BodyText, SearchInput } from '@paypalcorp/pp-react';
import parseContent from '../../../lib/parseContent';
import { getSearchOptions } from '../../../lib/search';
import { useScreenSizeInfo } from '../ScreenSizeInfoProvider';

const getResultCount = results => (results && results.length) || 0;
const getStringifiedResults = results =>
  Array.isArray(results) && results.length ? results.join(', ') : '';

const renderTextComp = (
  results,
  content,
  isSearchModalEligible,
  dictionary
) => {
  return (
    <BodyText size="md" medium>
      {results && results.length > 0
        ? parseContent(content.searchResult, dictionary)
        : !isSearchModalEligible && content.searchBody}
    </BodyText>
  );
};

const getPageLink = shouldShowModal =>
  shouldShowModal ? 'search_modal' : 'search';

const getBoolean = (isNewLayoutEligible, isSearchModalEligible) =>
  isNewLayoutEligible && isSearchModalEligible;

const getActiveClass = active => (active ? 'active' : '');

const getOldClass = isNewLayoutEligible => (!isNewLayoutEligible ? 'old' : '');

const getIsMobileBoolean = screenSize => screenSize && screenSize.isMobile;

const getResult = (searchResults, results) =>
  searchResults ? searchResults : results;

const getSearchValue = (value, query) => value || query || '';

const Search = props => {
  let searcher;
  let FlexSearch;
  const {
    app,
    tools = {},
    appSearch: { active, query, results },
    seoData = {},
    dispatch,
    updateSearchQuery,
    updateSearchResults,
    setSearchActive,
    content = {},
    fptiImpression,
    fptiClick,
    isNewLayoutEligible,
    experimentation
  } = props;
  const toolList = useMemo(() => Object.values(tools), [tools]);
  const { searchModalExp: isSearchModalEligible } = experimentation;

  const shouldShowModal = getBoolean(
    isNewLayoutEligible,
    isSearchModalEligible
  );
  // Get search results and update store
  const updateSearch = useCallback(
    // Only do search if user hasn't changed the input for 300 ms
    debounce(value => {
      return Promise.resolve(FlexSearch)
        .then(FlexSearch => {
          if (FlexSearch) {
            return searcher;
          }

          // Lazy load fuzzy-search
          return import('flexsearch').then(md => {
            FlexSearch = md.default;
            const searchOptions = getSearchOptions(
              get(app, `context.locale.language`)
            );

            searcher = new FlexSearch(searchOptions);
            toolList.forEach((tool, i) => {
              const { categoryKey, location } = tool;
              const seoCategory = categoryKey.replace('_', '');
              const seoKeywords = get(
                seoData,
                `${seoCategory}.${location}.keywords`
              );
              searcher.add(
                i,
                `${seoKeywords} ${tool.appTitle} ${tool.appBody}`
              );
            });
            return searcher;
          });
        })
        .then(searcher => {
          // Do the search and update the store
          const searchResults = searcher
            .search(value)
            .map(index => toolList[index].appKey);
          dispatch(updateSearchQuery(value));
          dispatch(updateSearchResults(searchResults));
          debounceSearchFpti(value, searchResults);
        });
    }, 300),
    [debounce, dispatch]
  );

  const [inputValue, setInputValue] = useState('');

  const onChange = ({ target: { value } }) => {
    setInputValue(value);
    updateSearch(value);
  };

  const handleClear = () => {
    setInputValue('');
    updateSearch('');
  };

  const onFocus = () => {
    if (active) {
      dispatch(setSearchActive(false));
      doSearchFpti('close');
    } else {
      dispatch(setSearchActive(true));
      doSearchFpti('open');
    }
  };
  const onBlur = () => {
    if (!inputValue) {
      dispatch(setSearchActive(false));
    }
    doSearchFpti('close');
  };

  const dictionary = {
    resultCount: getResultCount(results),
    query
  };

  const doSearchFpti = useCallback(
    (searchAction, value, searchResults) => {
      fptiClick({
        pageLink: getPageLink(shouldShowModal),
        fptiLink: `search_${searchAction}`,
        srch: getSearchValue(value, query),
        result: getStringifiedResults(getResult(searchResults, results))
      });
    },
    [fptiClick, query, results]
  );

  const debounceSearchFpti = useCallback(
    debounce((value, searchResults) => {
      const searchAction =
        Array.isArray(searchResults) && searchResults.length ? 'bar' : 'none';
      doSearchFpti(searchAction, value, searchResults);
    }, 1000),
    [debounce, query, results]
  );
  useEffect(() => {
    fptiImpression({ pageLink: 'search' });
  }, []);

  const [placeholder, setPlaceholder] = useState(content.searchTitle);
  const screenSize = useScreenSizeInfo();
  const isMobile = getIsMobileBoolean(screenSize);

  useEffect(() => {
    if (isMobile) {
      setPlaceholder(null);
      return;
    }
    setPlaceholder(content.searchTitle);
  }, [isMobile]);

  return (
    <div>
      <SearchInput
        label={content.searchTitle}
        name="search"
        value={inputValue}
        placeholder={placeholder}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onClear={handleClear}
        autoComplete="off"
        className={[
          'appcenter-search',
          getOldClass(isNewLayoutEligible),
          getActiveClass(active)
        ]}
        style={{
          borderRadius: '100rem',
          boxShadow: 'none',
          borderColor: '#909191'
        }}
        onClick={e => e.stopPropagation()}
      />
      {query
        ? renderTextComp(results, content, isSearchModalEligible, dictionary)
        : null}
    </div>
  );
};

Search.propTypes = {
  app: PropTypes.object,
  tools: PropTypes.object.isRequired,
  appSearch: PropTypes.shape({
    active: PropTypes.bool,
    query: PropTypes.string,
    results: PropTypes.arrayOf(PropTypes.string)
  }),
  seoData: PropTypes.object,
  dispatch: PropTypes.func,
  updateSearchQuery: PropTypes.func,
  updateSearchResults: PropTypes.func,
  clearSearch: PropTypes.func.isRequired,
  setSearchActive: PropTypes.func,
  content: PropTypes.shape({
    searchTitle: PropTypes.string.isRequired,
    searchResult: PropTypes.string,
    searchBody: PropTypes.string.isRequired
  }).isRequired,
  fptiImpression: PropTypes.func,
  fptiClick: PropTypes.func,
  isNewLayoutEligible: PropTypes.bool,
  experimentation: PropTypes.object,
  searchModalExp: PropTypes.bool
};

export default Search;
